import { Badge, Group, Select, SimpleGrid, Stack, Text, Textarea, TextInput } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import dayjs from 'dayjs'
import { Controller, useWatch, type Control } from 'react-hook-form'
import { CAQH_PROFILE_STATUSES } from '@behuman/core-contract'
import type { CaqhFormValues } from './ProviderPage'

/**
 * The CAQH tab's controls: the CAQH provider id, the Profile Status, the two attestation dates
 * and the notes, every field optional. Badged Overdue once the re-attestation due date is
 * before today's local date, whatever the status says; derived here, never stored.
 */
export function CaqhProfileField({ control }: { control: Control<CaqhFormValues> }) {
  const reattestationDue = useWatch({ control, name: 'caqh.reattestationDue' })
  const overdue = Boolean(reattestationDue) && dayjs(reattestationDue).isBefore(dayjs(), 'day')

  const dateField = (key: 'lastAttestation' | 'reattestationDue', label: string) => (
    <Controller
      name={`caqh.${key}`}
      control={control}
      render={({ field, fieldState }) => (
        <DatePickerInput
          {...field}
          value={field.value ?? null}
          label={label}
          placeholder={label}
          clearable
          clearButtonProps={{ 'aria-label': `Clear ${label}` }}
          error={fieldState.error?.message}
        />
      )}
    />
  )

  return (
    <Stack>
      <div>
        <Group justify="space-between" align="center">
          <Text size="sm" fw={500}>
            CAQH profile
          </Text>
          {overdue && (
            <Badge color="red" variant="light">
              Overdue
            </Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          A current CAQH ProView profile is what payers draw on when credentialing this member.
        </Text>
      </div>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
        <Controller
          name="caqh.providerId"
          control={control}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              value={field.value ?? ''}
              label="CAQH provider ID"
              placeholder="CAQH provider ID"
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="caqh.status"
          control={control}
          render={({ field, fieldState }) => (
            <Select
              {...field}
              value={field.value ?? null}
              clearable
              label="Profile status"
              placeholder="Not recorded"
              clearButtonProps={{ 'aria-label': 'Clear Profile status' }}
              data={[...CAQH_PROFILE_STATUSES]}
              error={fieldState.error?.message}
            />
          )}
        />
        {dateField('lastAttestation', 'Last attestation')}
        {dateField('reattestationDue', 'Re-attestation due')}
      </SimpleGrid>
      <Controller
        name="caqh.notes"
        control={control}
        render={({ field, fieldState }) => (
          <Textarea
            {...field}
            value={field.value ?? ''}
            label="Notes"
            placeholder="Notes"
            autosize
            minRows={3}
            error={fieldState.error?.message}
          />
        )}
      />
    </Stack>
  )
}
